"use client";

import type { ReactNode } from "react";

import {
  FONT_MONO,
  TEXT_MUTE,
  VIOLET_BRIGHT,
} from "@/components/ui/organisms/chord-practice/palette";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { ARRIVE, EASE_MOVE, EASE_OUT, SHIFT, ease, pick } from "../motion";

interface PieceEyebrowProps {
  noteCount: number;
  notesPerMeasure: number;
  measures: number;
  durationLabel: string;
  /** La fiche est retournée sur ses deux parcours. */
  compact: boolean;
  /** Replier le choix et revenir au morceau. */
  onBack: () => void;
}

/**
 * Top row of the PieceOverview overlay. Houses the secondary
 * "informational" stats now that the centre block carries the musical
 * identity (tonality / signature / tempo / tessitura).
 *
 * Left  : a single "MORCEAU" label, ancrage discret du contexte.
 * Right : the raw numerical stats, note count, density, total
 *         measures, duration, in a compact mono / uppercase line.
 *
 * ## C'est la bande qui ne bouge pas
 *
 * Quand la fiche se retourne sur ses parcours, tout monte : la titraille de
 * cinquante pixels, le corps du texte de cent. Cette bande-ci descend de
 * quatorze, change de mots, et c'est tout. Sans un point fixe, un mouvement
 * d'ensemble n'a rien contre quoi se mesurer.
 *
 * Les deux mots se remplacent en fondu croisé, dans la même case de grille :
 * une bascule sèche se lirait comme un autre écran, et une case qui se
 * redimensionne ferait sauter la ligne.
 */
export function PieceEyebrow({
  noteCount,
  notesPerMeasure,
  measures,
  durationLabel,
  compact,
  onBack,
}: PieceEyebrowProps) {
  const reduced = useReducedMotion();
  const shift = pick(SHIFT, compact);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: `${compact ? 14 : 28}px 40px 0`,
        gap: 16,
        transition: reduced ? "none" : ease("padding-top", shift, EASE_MOVE),
      }}
    >
      <Swap
        compact={compact}
        beat={ARRIVE.overline}
        piece={<Overline>Morceau</Overline>}
        program={<Overline>Programme d&apos;entraînement</Overline>}
      />

      <Swap
        compact={compact}
        beat={ARRIVE.back}
        align="end"
        piece={
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              color: TEXT_MUTE,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {formatNoteCount(noteCount)} notes · {formatDensity(notesPerMeasure)} / mes ·{" "}
            {measures} mesures · {durationLabel}
          </div>
        }
        program={<BackToPiece onClick={onBack} />}
      />
    </div>
  );
}

/** Le surtitre : le cadre dans lequel le titre se lit. */
function Overline({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 10,
        color: VIOLET_BRIGHT,
        opacity: 0.6,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Deux contenus dans la même case, dont un seul se voit.
 *
 * Empilés en `grid-area: 1/1` plutôt que montés puis démontés : la case garde
 * alors la largeur du plus large des deux, donc la ligne ne saute pas au milieu
 * du fondu, et les deux textes se croisent au lieu de se succéder.
 *
 * Celui qui ne se voit pas est aussi hors d'atteinte du clavier : le geste de
 * retour ne doit pas se tabuler depuis la fiche, où il ne mène nulle part.
 */
function Swap({
  compact,
  beat,
  piece,
  program,
  align = "start",
}: {
  readonly compact: boolean;
  readonly beat: { readonly delay: number; readonly ms: number };
  readonly piece: ReactNode;
  readonly program: ReactNode;
  readonly align?: "start" | "end";
}) {
  const reduced = useReducedMotion();

  const layer = (shown: boolean): React.CSSProperties => ({
    gridArea: "1 / 1",
    justifySelf: align,
    opacity: shown ? 1 : 0,
    transform: shown || reduced ? "none" : "translateY(4px)",
    transition: reduced
      ? "none"
      : [ease("opacity", beat, EASE_OUT), ease("transform", beat, EASE_OUT)].join(", "),
  });

  return (
    <div style={{ display: "grid", minWidth: 0 }}>
      <div inert={compact} style={layer(!compact)}>
        {piece}
      </div>
      <div inert={!compact} style={layer(compact)}>
        {program}
      </div>
    </div>
  );
}

/**
 * Replier le choix, à la place exacte des chiffres.
 *
 * À droite et non à gauche, malgré la convention : à gauche il y a le surtitre,
 * et surtout il n'y a pas de page précédente. C'est le même écran qui se
 * replie. Posé à droite, en petit, là où il y avait des données, il se lit
 * comme « replier » et non comme « naviguer ».
 *
 * En violet et non en gris, parce que le violet est ce qui se donne pour
 * cliquable dans cette page. La bande devient alors violette des deux côtés :
 * l'identité à gauche, la sortie à droite.
 */
function BackToPiece({ onClick }: { readonly onClick: () => void }) {
  const nudge = (target: HTMLElement, by: string) => {
    const arrow = target.querySelector<HTMLSpanElement>("[data-arrow]");

    if (arrow !== null) {
      arrow.style.transform = `translateX(${by})`;
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center rounded-md transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none"
      style={{
        gap: 8,
        padding: "2px 4px",
        border: "none",
        background: "transparent",
        fontFamily: FONT_MONO,
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color: VIOLET_BRIGHT,
        opacity: 0.6,
        cursor: "pointer",
      }}
      onMouseEnter={(event) => nudge(event.currentTarget, "-2px")}
      onMouseLeave={(event) => nudge(event.currentTarget, "0")}
      onFocus={(event) => nudge(event.currentTarget, "-2px")}
      onBlur={(event) => nudge(event.currentTarget, "0")}
    >
      <span
        aria-hidden
        data-arrow
        className="inline-block transition-transform duration-200 motion-reduce:transition-none"
      >
        ←
      </span>
      Le morceau
    </button>
  );
}

function formatNoteCount(count: number): string {
  if (count <= 0) {
    return ", ";
  }

  return count.toLocaleString("fr-FR");
}

function formatDensity(perMeasure: number): string {
  if (!Number.isFinite(perMeasure) || perMeasure <= 0) {
    return ", ";
  }

  return perMeasure.toFixed(1);
}
